import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBarcode, FaBox, FaBoxOpen, FaCalendarAlt, FaChartLine, FaCog, FaEdit, FaEnvelope, FaEye, FaFileDownload, FaFileUpload, FaList, FaMapMarkerAlt, FaMoneyBill, FaMoneyBillWave, FaPlus, FaPrint, FaQrcode, FaReceipt, FaSearch, FaStore, FaTag, FaTicketAlt, FaTrash, FaUndoAlt, FaUser, FaWhatsapp } from 'react-icons/fa';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Pagination, PaginationItem, PaginationLink, Row, Table, Toast, ToastBody, ToastHeader } from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { productActions } from '../../sagas/productSlice';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { saleActions } from '../../sagas/saleSlice';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useNavigate } from 'react-router-dom';
import { refundActions } from '../../sagas/refundSlice';
const RefundsReport = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [criteria, setSearchCriteria] = useState({ query: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const {
        refunds,
        isLoading,
        totalPages,
        totalElements,
        pageNumber,
        pageSize,
        error,
    } = useSelector((state) => state.refunds);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [selectedRefund, setSelectedRefund] = useState(null);
    const [showItemsModal, setShowItemsModal] = useState(false);
    const { businessName, email, address, telephone, fullName } = useSelector(state => state.auth);
    const navigate = useNavigate();


    // Add handler
    const handleViewDetails = (sale) => {
        setSelectedRefund(sale);
        setShowReceiptModal(true);
    };

    useEffect(() => {
        let payload = {
            criteria,
            page: currentPage,
            size: pageSize,
        };
        dispatch(refundActions.search(payload));
    }, [currentPage]);


    // Debounced search function
    const debouncedSearch = useCallback(
        debounce((query) => {
            setIsSearching(true);
            dispatch(refundActions.search({
                criteria: { query },
                page: 1,
                size: pageSize,
                onSuccess: () => setIsSearching(false)
            }));
        }, 1000), // Wait 500ms after last keystroke
        []
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Update price formatting function
    const formatPrice = (amount) => {
        const formattedPrice = new Intl.NumberFormat('fr-MA', {
            style: 'decimal',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
        return `${formattedPrice} DH`;
    };

    const handlePrint = () => {
        const element = document.getElementById('receipt-content');
        html2canvas(element).then((canvas) => {
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF();
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.autoPrint();
            window.open(pdf.output('bloburl'), '_blank');
        });
    };

    return (
        <Container fluid>
            <Row className="mb-3">
                <Col>
                    <Card className="shadow-sm border-0">
                        <CardBody className="py-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h4 className="mb-0 d-flex align-items-center">
                                    <FaChartLine className="text-primary me-2" />
                                    {t('refund.pageTitle')}
                                </h4>
                                <Button
                                    color="primary"
                                    className="d-flex align-items-center"
                                    onClick={() => navigate('/refund')}
                                    title={t('refund.newRefund')}
                                >
                                    <FaPlus className="me-2" />
                                    {t('refund.newRefund')}
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col md={12} className="pe-4">
                    <Card className="shadow-sm h-100">
                        <CardBody>
                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t('common.loading')}</span>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        {t('common.loadingData')}
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <div className="position-relative flex-grow-1 me-3">
                                            <Input
                                                type="text"
                                                placeholder={t('refund.searchPlaceholder')}
                                                value={searchTerm}
                                                onChange={handleSearchChange}
                                                className={`ps-4 ${isSearching ? 'searching' : ''}`}
                                            />
                                            <FaSearch
                                                style={{
                                                    position: 'absolute',
                                                    left: '10px',
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    color: '#adb5bd'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="table-responsive">
                                        <Table hover bordered>
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>{t('refund.number')}</th>
                                                    <th>{t('refund.date')}</th>
                                                    <th className="text-end">{t('refund.total')}</th>
                                                    <th className="text-center">{t('common.actions')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {refunds.map((refund) => (
                                                    <tr key={refund.id}>
                                                        <td>{refund.numero}</td>
                                                        <td>{refund.refundDate}</td>
                                                        <td className="text-end">{formatPrice(refund.total)}</td>
                                                        <td className="text-center">
                                                            <Button
                                                                color="info"
                                                                size="sm"
                                                                className="me-2"
                                                                onClick={() => handleViewDetails(refund)}
                                                            >
                                                                <FaEye size={14} />
                                                            </Button>
                                                            {/* <Button
                                                                color="danger"
                                                                size="sm"
                                                                onClick={() => handleDeleteRefund(refund.id)}
                                                            >
                                                                <FaTrash size={14} />
                                                            </Button> */}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>

                                    {/* Pagination */}
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <small className="text-muted">
                                            {t('product.showing', {
                                                start: (currentPage - 1) * pageSize + 1,
                                                end: Math.min(currentPage * pageSize, totalElements),
                                                total: totalElements,
                                                currentPage: currentPage,
                                                totalPages: totalElements
                                            })}
                                        </small>
                                        <Pagination size="sm" className="mb-0">
                                            <PaginationItem disabled={currentPage === 1}>
                                                <PaginationLink first onClick={() => handlePageChange(1)}>
                                                    <FaAngleDoubleLeft size={12} />
                                                </PaginationLink>
                                            </PaginationItem>
                                            <PaginationItem disabled={currentPage === 1}>
                                                <PaginationLink previous onClick={() => handlePageChange(currentPage - 1)}>
                                                    <FaAngleLeft size={12} />
                                                </PaginationLink>
                                            </PaginationItem>
                                            <PaginationItem disabled={currentPage === totalPages}>
                                                <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
                                                    <FaAngleRight size={12} />
                                                </PaginationLink>
                                            </PaginationItem>
                                            <PaginationItem disabled={currentPage === totalPages}>
                                                <PaginationLink last onClick={() => handlePageChange(totalPages)}>
                                                    <FaAngleDoubleRight size={12} />
                                                </PaginationLink>
                                            </PaginationItem>
                                        </Pagination>
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Modal isOpen={showReceiptModal} toggle={() => setShowReceiptModal(false)}>
                <ModalHeader toggle={() => setShowReceiptModal(false)}>
                    {t('refund.receipt.title')}
                </ModalHeader>
                <ModalBody>
                    <div id="receipt-content" className="p-4 bg-white shadow-sm rounded" style={{ width: '100%' }}>
                        {/* Header */}
                        <div className="text-center mb-4">
                            <h5 className="mb-2">
                                <FaStore className="me-2 text-secondary" />
                                {businessName}
                            </h5>
                            <small className="d-block mb-2">
                                <FaMapMarkerAlt className="me-1 text-secondary" />
                                {address}
                            </small>
                            <div className="d-flex justify-content-center align-items-center gap-3">
                                {telephone && (
                                    <small>
                                        <FaWhatsapp className="me-1 text-secondary" />
                                        {telephone}
                                    </small>
                                )}
                                <small>
                                    <FaEnvelope className="me-1 text-secondary" />
                                    {email}
                                </small>
                            </div>
                        </div>

                        <hr />

                        {/* Refund Details */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <div>
                                    <FaUndoAlt className="me-2 text-secondary" />
                                    <span className="fw-bold">{t('refund.receipt.refundNo')} </span>
                                    {selectedRefund?.numero}
                                </div>
                                <div>
                                    <FaCalendarAlt className="me-2 text-secondary" />
                                    <span className="fw-bold">{t('refund.receipt.date')} </span>
                                    {selectedRefund?.refundDate}
                                </div>
                            </div>
                            <div>
                                <FaUser className="me-2 text-secondary" />
                                <span className="fw-bold">{t('refund.receipt.cashier')} </span>
                                {fullName}
                            </div>
                        </div>

                        <hr />

                        {/* Refunded Items Table */}
                        <table className="table table-sm">
                            <thead>
                                <tr>
                                    <th>{t('product.description')}</th>
                                    <th className="text-end">{t('refund.receipt.price')}</th>
                                    <th className="text-end">{t('refund.receipt.quantity')}</th>
                                    <th className="text-end">{t('refund.receipt.total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedRefund?.items.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.productDescription}</td>
                                        <td className="text-end">{formatPrice(item.price)}</td>
                                        <td className="text-end">{item.quantity}</td>
                                        <td className="text-end">{formatPrice(item.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <hr />

                        {/* Total */}
                        <div className="text-end mb-4">
                            <div className="mb-2">
                                <span className="fw-bold me-3">{t('refund.receipt.totalRefunded')}</span>
                                {formatPrice(selectedRefund?.total)}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="text-center mt-4">
                            <p className="mb-1">{t('refund.receipt.thankYou')}</p>
                            <small className="text-muted">{t('refund.receipt.footer')}</small>
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handlePrint}>
                        <FaPrint className="me-1" />
                        {t('refund.receipt.print')}
                    </Button>
                </ModalFooter>
            </Modal>
            
        </Container>

    );
};

export default RefundsReport;
